import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Collapse, Empty, Spin } from 'antd';
import PropTypes from 'prop-types';
import { DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import DashboardComponent from './DashboardComponent';
import Permissions from '../auth/Permissions';
import BorderlessButton from '../common/buttons/BorderlessButton';
import CustomConfirmModal from '../common/modals/CustomConfirmModal';
import {
  deleteShortListItem,
  getBoardDetails,
  getBoards,
  getCardById,
  getShortList,
  openCardDrawer,
  openFormMapDrawer,
  reorderShortListItems,
} from '../boards/state/boards.actions';
import useToggle from '../common/hooks/useToggle';
import ShortListSettingsModal from '../boards/ShortLists/ShortListSettingsModal';
import DraggableTable from '../projects/ProjectScheduleOfValues/DraggableTable';
import { textBasedFieldTypes } from '../forms/formHelpers';
import { parseCustomResponses } from '../boards/boards.helpers';
import { getIdMap } from '../helpers/helpers';

const { Panel } = Collapse;

export default function DashboardShortListWidget({
  shortListId,
  onDelete,
  isEdit,
  setSelectedBoard,
  setCardType,
}) {
  const dispatch = useDispatch();
  const shortLists = useSelector((state) => state.boards.shortLists);
  const shortListCategoryMap = useSelector((state) => state.boards.shortListCategoryMap);
  const shortListItemMap = useSelector((state) => state.boards.shortListItemMap);
  const shortListPermissionMap = useSelector((state) => state.boards.shortListPermissionMap);
  const boards = useSelector((state) => state.boards.boards);
  const cardTypes = useSelector((state) => state.boards.cardTemplates);
  const { settings = {} } = useSelector((state) => state.settings.company);
  const projects = useSelector((state) => state.projects.projects);

  const [activeKeys, setActiveKeys] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const {
    toggle: toggleEdit,
    isToggled: isEditMode,
  } = useToggle();

  const ourBoard = useMemo(() => (
    boards[selectedItem?.boardId]
  ), [boards, selectedItem]);

  const cardType = useMemo(() => (
    cardTypes[ourBoard?.cardTypeId]
  ), [cardTypes, ourBoard]);

  const projectIdMap = useMemo(() => getIdMap(projects), [projects]);

  useEffect(() => {
    setSelectedBoard(ourBoard);
    setCardType(cardType);
  }, [ourBoard, cardType]);

  useEffect(() => {
    const loadShortList = async () => {
      setLoading(true);
      dispatch(getBoards());
      await dispatch(getShortList(shortListId));
      setLoading(false);
    };

    if (shortListId) loadShortList();
  }, [shortListId]);

  useEffect(() => {
    if (selectedItem) dispatch(getBoardDetails(selectedItem.boardId));
  }, [selectedItem]);

  const relevantShortList = useMemo(() => (
    shortLists.find((shortList) => shortList.id === shortListId)
  ), [shortLists, shortListId]);

  const relevantCategories = useMemo(() => (
    shortListCategoryMap?.[shortListId] || []
  ), [shortListCategoryMap, shortListId]);

  const {
    relevantItems,
    selectedBoardIds,
  } = useMemo(() => {
    const selectedBoards = new Set();
    const items = (shortListItemMap?.[shortListId] || []).reduce((acc, item) => {
      const relevantCategoryId = item.boardShortListCategoryId || 'unassigned';
      if (!acc[relevantCategoryId]) {
        acc[relevantCategoryId] = [];
      }
      acc[relevantCategoryId].push(item);
      selectedBoards.add(item.boardId);
      return acc;
    }, {});

    return {
      relevantItems: items,
      selectedBoardIds: Array.from(selectedBoards),
    };
  }, [shortListItemMap, shortListId]);

  const {
    fieldOptions: relevantFieldOptions,
    fieldMap: relevantFieldMap,
  } = useMemo(() => {
    const selectedCardTypes = new Set(
      selectedBoardIds.map((boardId) => (
        cardTypes[boards[boardId]?.cardTypeId]
      )).filter(Boolean),
    );

    const customTreeData = [
      {
        value: '-1',
        title: 'Native Fields',
        selectable: false,
        children: [
          { label: 'Card Name', value: 'title' },
          { label: 'Description', value: 'description' },
          { label: 'Card Status', value: 'status' },
        ],
      },
    ];
    const fieldMap = {
      title: {
        name: 'Card Name',
        id: 'title',
      },
      description: {
        name: 'Description',
        id: 'description',
      },
    };

    Array.from(selectedCardTypes).forEach(({ fields: sections = [], id, title }) => {
      const children = [];
      sections?.forEach(({ fields = [], name: sectionName }) => {
        fields?.forEach((field) => {
          if (textBasedFieldTypes.has(field.selectedType)) {
            const fullFieldName = `${sectionName} - ${field?.configProps?.title}`;
            children.push({ label: fullFieldName, value: field.id });
            fieldMap[field.id] = {
              name: fullFieldName,
              id: field.id,
              cardTypeId: id,
              configProps: field.configProps,
            };
          }
        });
      });

      customTreeData.push({
        value: id,
        title,
        selectable: false,
        children,
      });
    });

    return {
      fieldOptions: customTreeData,
      fieldMap,
    };
  }, [boards, selectedBoardIds, cardTypes]);

  const renderDataIndex = useCallback((record, dataIndex) => {
    if (Array.isArray(dataIndex)) {
      return dataIndex
        .map((subDataIndex) => renderDataIndex(record, subDataIndex))
        .filter(Boolean)
        .join(', ');
    }

    if (!dataIndex?.startsWith('field-')) {
      return record[dataIndex];
    }

    const templateField = relevantFieldMap[dataIndex];
    const {
      type,
      response,
    } = record[dataIndex] || {};

    if (!templateField || !type || !response) return '';
    return parseCustomResponses({
      field: {
        ...templateField,
        type,
        response,
      },
      projectIdMap,
      settings,
    });
  }, [relevantFieldMap, settings]);

  const isOwner = useMemo(() => (
    relevantShortList?.userId === Permissions.id
  ), [relevantShortList]);

  const shortListPermissions = shortListPermissionMap?.[shortListId] || [];
  const hasEdit = isOwner || shortListPermissions.some(
    (permission) => permission.userId === Permissions.id && permission.canEdit,
  );

  const onDeleteItem = useCallback((record) => {
    CustomConfirmModal({
      title: 'Delete Short List Item',
      content: 'Are you sure you want to remove this item from the short list?',
      onOk: () => dispatch(deleteShortListItem(shortListId, record.id)),
    });
  }, [shortListId]);

  const onForm = useCallback((cardId) => {
    dispatch(openFormMapDrawer({
      selectedCardId: cardId,
    }));
  }, []);

  const columns = useMemo(() => {
    try {
      const parsedDisplayFields = JSON.parse(relevantShortList?.displayFields || []);
      const baseColumns = parsedDisplayFields.map((field) => ({
        title: field.title,
        dataIndex: field.dataIndex,
        render: (_, record) => renderDataIndex(record, field.dataIndex),
      }));

      baseColumns.push({
        title: '',
        width: 30,
        dataIndex: 'view',
        render: (_, record) => (
          <BorderlessButton
            iconNode={<EyeOutlined />}
            onClick={() => {
              setSelectedItem(record);
              dispatch(openCardDrawer({
                selectedCardId: record.cardId,
                onForm,
              }));
              dispatch(getCardById(record.cardId));
            }}
          />
        ),
      });

      if (hasEdit) {
        baseColumns.push({
          title: '',
          width: 30,
          dataIndex: 'delete',
          render: (_, record) => <BorderlessButton iconNode={<DeleteOutlined style={{ color: 'red' }} />} onClick={() => onDeleteItem(record)} />,
        });
      }

      return baseColumns;
    } catch (err) {
      return [];
    }
  }, [relevantShortList, hasEdit, onDeleteItem, renderDataIndex, onForm]);

  const {
    baseActiveKeys,
    categoryKeyMap,
  } = useMemo(() => {
    const allCategories = (relevantCategories?.map((category) => category.id) || []).concat('unassigned');
    const keyMap = allCategories?.reduce((acc, category) => {
      acc[category] = category;
      return acc;
    }, {});
    keyMap.unassigned = 'unassigned';
    return {
      baseActiveKeys: allCategories,
      categoryKeyMap: keyMap,
    };
  }, [relevantCategories]);

  useEffect(() => {
    setActiveKeys(baseActiveKeys);
  }, [relevantCategories]);

  const onSetData = useCallback((_, data) => {
    const payload = data.map((item, index) => ({
      id: item.id,
      orderIndex: index,
      boardShortListCategoryId: item.boardShortListCategoryId === 'unassigned' ? null : item.boardShortListCategoryId,
    }));

    dispatch(reorderShortListItems(shortListId, { items: payload }));
  }, []);

  const onMoveBetweenTables = useCallback((
    sourceIndex,
    targetIndex,
    sourceTableType,
    targetTableType,
  ) => {
    if (sourceTableType === targetTableType) return;
    const relevantItem = relevantItems?.[sourceTableType]?.[sourceIndex];
    const originalCategory = (relevantItems?.[sourceTableType] || [])
      .filter((_, index) => index !== sourceIndex)
      .map((item, index) => ({
        id: item.id,
        orderIndex: index,
        boardShortListCategoryId: sourceTableType === 'unassigned' ? null : sourceTableType,
      }));

    const originalTargetCategory = (relevantItems?.[targetTableType] || []);
    const newCategory = [
      ...originalTargetCategory.slice(0, targetIndex),
      relevantItem,
      ...originalTargetCategory.slice(targetIndex),
    ].map((item, index) => ({
      id: item.id,
      orderIndex: index,
      boardShortListCategoryId: targetTableType === 'unassigned' ? null : targetTableType,
    }));

    dispatch(reorderShortListItems(shortListId, {
      items: [...originalCategory, ...newCategory],
    }));
  }, [relevantItems]);

  return (
    <DashboardComponent
      title={relevantShortList?.name}
      onDelete={onDelete}
      isEdit={isEdit}
      onEdit={isOwner && !loading ? toggleEdit : null}
      headerStyle={{ height: 50 }}
    >
      { !loading && (
        <div
          style={{
            height: 'calc(100% - 40px)',
            width: '100%',
            overflowY: 'auto',
            paddingTop: 20,
          }}
        >
          { !!relevantShortList && (
            <DndProvider backend={HTML5Backend}>
              <Collapse activeKey={activeKeys} onChange={setActiveKeys}>
                {relevantCategories?.map((category) => (
                  <Panel
                    header={category.name}
                    key={category.id}
                  >
                    <DraggableTable
                      dataSource={relevantItems?.[category.id] || []}
                      columns={columns}
                      pagination={false}
                      size="small"
                      rowKey="id"
                      isDraggable={hasEdit}
                      enableCrossTableDrag
                      type={category.id}
                      setData={onSetData}
                      dropKeyMap={categoryKeyMap}
                      onMoveBetweenTables={onMoveBetweenTables}
                      onCLick
                    />
                  </Panel>
                ))}
                <Panel header="Unassigned" key="unassigned">
                  <DraggableTable
                    dataSource={relevantItems?.unassigned || []}
                    columns={columns}
                    pagination={false}
                    size="small"
                    rowKey="id"
                    isDraggable={hasEdit}
                    enableCrossTableDrag
                    type="unassigned"
                    setData={onSetData}
                    dropKeyMap={categoryKeyMap}
                    onMoveBetweenTables={onMoveBetweenTables}
                  />
                </Panel>
              </Collapse>
            </DndProvider>
          )}
          { !relevantShortList && (
            <div>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="This short list may have been removed, or you may no longer have the necessary permissions to access it."
              />
            </div>
          )}
        </div>
      )}
      { loading && (
        <div
          style={{
            height: '100%',
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Spin size="large" />
        </div>
      )}
      <ShortListSettingsModal
        visible={isEditMode}
        onClose={toggleEdit}
        shortList={relevantShortList}
        onDelete={onDelete}
        fieldOptions={relevantFieldOptions}
        fieldMap={relevantFieldMap}
      />
    </DashboardComponent>
  );
}

DashboardShortListWidget.propTypes = {
  shortListId: PropTypes.string.isRequired,
  onDelete: PropTypes.func.isRequired,
  isEdit: PropTypes.bool.isRequired,
  setSelectedBoard: PropTypes.func.isRequired,
  setCardType: PropTypes.func.isRequired,
};
